import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Content, ApiErrorComponentLazyLoad } from '@edikk-edikk/country-layout-black';
import { ContentLoader } from '@edikk-edikk/country-common';
import { useErrorBoundary } from 'react-error-boundary';
import { ReCaptcha } from '@edikk-edikk/recaptcha';
import {
  BotDetectorContext,
  BotDetectorContextType,
  FrontendStyleContext,
  FrontendStyleContextType,
} from '@edikk-edikk/country-frontend';
import {
  queryParse,
  SettingsContext,
  SettingsContextType,
  UserContext,
  UserContextType,
} from '@edikk-edikk/shared';
import { Ga4ViaPartytown } from '@edikk-edikk/ga4';
import loadable from '@loadable/component';
import { useApi } from '#hooks/use-api';
import { BaseContext } from '#base/contexts/BaseContext';
import { BaseContextType } from '#base/contexts/BaseContextType';
import { Routers } from '#base/components/Routers';
import { AgeGateWrapper } from '#baseAgeGate/containers/AgeGateWrapper';
import {
  BodyAttributes,
  Favicon,
  Footer,
  GoogleSiteVerification,
  Noindex,
} from '#base/components';
import { NotifierWrapper } from '#base/components/NotifierWrapper';
import { Menu, MenuActionControlProvider } from '#baseMenu/containers';

const ApiErrorComponent = loadable(() => import('@edikk-edikk/country-layout-black/error/api/ApiErrorComponent'), {
  resolveComponent: ({ ApiErrorComponent: Component }) => Component,
});

const App: FC = () => {
  const [isInit, setIsInit] = useState<boolean>(false);
  const location = useLocation();
  const api = useApi();
  const {
    data,
    isFetching,
    isError,
    error,
  } = api.getApiBase().useInitQuery(
    {
      slug: location.pathname,
      query: queryParse(location.search),
    },
    { skip: isInit },
  );
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    setIsInit(!isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (error) {
      showBoundary(error as Error);
    }
  }, [error, showBoundary]);

  if (isFetching || isError) {
    return <ContentLoader />;
  }

  const baseContextValue: BaseContextType = {
    routerConfig: data.routerConfig,
    languageData: data.languageData,
    catalogMode: data.catalogMode,
    footerLinks: data.footerLinks,
    ageGate: data.ageGate,
    hasCityList: data.hasCityList,
    googleConfig: data.googleConfig,
    favicon: data.favicon,
    isSingleCity: data.isSingleCity,
    linkToManage: data.linkToManage,
    domain: data.domain,
    noindex: data.noindex,
  };

  const userContextValue: UserContextType = {
    isAuth: data.isAuth,
  };

  const settingsContextValue: SettingsContextType = {
    ...data.settings,
  };

  const botDetectorContextValue: BotDetectorContextType = {
    isBot: data.isBot,
  };

  const frontendStyleContextValue: FrontendStyleContextType = {
    frontendStyle: data.frontendStyle,
  };

  return (
    <BaseContext value={baseContextValue}>
      <UserContext value={userContextValue}>
        <SettingsContext value={settingsContextValue}>
          <BotDetectorContext value={botDetectorContextValue}>
            <FrontendStyleContext value={frontendStyleContextValue}>
              <Noindex />
              <BodyAttributes />
              <Favicon />
              <GoogleSiteVerification />
              <AgeGateWrapper />
              <Ga4ViaPartytown measurementId={data.googleConfig?.measurementId} />
              <ReCaptcha isEnable={data.settings.reCaptcha.isEnable} site={data.settings.reCaptcha.site}>
                <NotifierWrapper />
                <Content>
                  <Routers />
                  <ApiErrorComponentLazyLoad ApiErrorComponent={ApiErrorComponent} />
                  <Footer />
                </Content>
                <MenuActionControlProvider>
                  <Menu />
                </MenuActionControlProvider>
              </ReCaptcha>
            </FrontendStyleContext>
          </BotDetectorContext>
        </SettingsContext>
      </UserContext>
    </BaseContext>
  );
};

export { App };
